import { HHButton, HHStack } from '@hinge-health/react-component-library';
import { useRef, useState } from 'react';
import { usePermissions } from '../context/permissions-context';
import { ControlAction, FileProcessingStatus } from '../domain/enums';
import { ControlResult } from '../domain/types';
import { ControlActionConfirmationDialog } from './control-action-confirmation-dialog';

const ACTION_ERROR_MESSAGES: Record<ControlAction, string> = {
  [ControlAction.Pause]: 'Failed to pause this ingestion. Please try again.',
  [ControlAction.Resume]: 'Failed to resume this ingestion. Please try again.',
  [ControlAction.Cancel]: 'Failed to cancel this ingestion. Please try again.',
};

export const PAUSE_INGESTION_BUTTON_TEST_ID = 'ingestion-header-pause-button';
export const RESUME_INGESTION_BUTTON_TEST_ID = 'ingestion-header-resume-button';
export const CANCEL_INGESTION_BUTTON_TEST_ID = 'ingestion-header-cancel-button';

/**
 * Which controls PDGS will actually accept, mirroring `ALLOWED_ACTIONS` in
 * its `ingestion-control-policy.ts`. Offering a button PDGS rejects turns a
 * policy error into a dead-end for the operator, so these lists have to
 * track that file.
 *
 * Notable asymmetries, all deliberate upstream:
 *
 * - `VALIDATING` / `RESOLVING` allow CANCEL but *not* PAUSE: the parent's
 *   pause signal only gates the batch loop and is not forwarded to the
 *   resolution child workflows, so a Pause button there would report
 *   "paused" while the children kept working.
 * - `FINALIZING` allows neither. Finalization is the sole outbox writer, and
 *   once it starts a cancel cannot take effect — publishing every record
 *   under a CANCELLED row would misreport a fully-processed file and invite
 *   a re-drive that duplicates outbox events.
 * - `PENDING` allows CANCEL: no workflow is running yet, so there is nothing
 *   to pause, but the queued file can still be dropped.
 */
const PAUSABLE_STATUSES: FileProcessingStatus[] = [
  FileProcessingStatus.Parsing,
];

const RESUMABLE_STATUSES: FileProcessingStatus[] = [
  FileProcessingStatus.Paused,
];

const CANCELLABLE_STATUSES: FileProcessingStatus[] = [
  FileProcessingStatus.Pending,
  FileProcessingStatus.Parsing,
  FileProcessingStatus.Validating,
  FileProcessingStatus.Resolving,
  FileProcessingStatus.Paused,
];

interface IngestionControlActionsProps {
  fileName: string;
  status: FileProcessingStatus;
  isControlling: boolean;
  controlIngestion: (action: ControlAction) => Promise<ControlResult>;
}

/**
 * Pause / Resume / Cancel controls for an ingestion, gated on
 * `permissions.canAdmin` and the ingestion's current status. Each action
 * requires confirmation before it fires. Refetching afterwards is the
 * responsibility of `useIngestionDetail.controlIngestion`, which already
 * refetches on success — doing it here too would double every control GET.
 */
export const IngestionControlActions = ({
  fileName,
  status,
  isControlling,
  controlIngestion,
}: IngestionControlActionsProps): JSX.Element | null => {
  const { canControlIngestions } = usePermissions();
  const [pendingAction, setPendingAction] = useState<ControlAction | null>(
    null,
  );
  const [actionError, setActionError] = useState<string | null>(null);
  // Guards against a fast double-click firing the mutation twice: `isControlling`
  // only flips true after the mutation promise starts, which lags one tick
  // behind a synchronous second click.
  const isSubmittingRef = useRef(false);

  const canPause = PAUSABLE_STATUSES.includes(status);
  const canResume = RESUMABLE_STATUSES.includes(status);
  const canCancel = CANCELLABLE_STATUSES.includes(status);

  if (!canControlIngestions || (!canPause && !canResume && !canCancel)) {
    return null;
  }

  const selectAction = (action: ControlAction): void => {
    setActionError(null);
    setPendingAction(action);
  };

  const handleCancelDialog = (): void => {
    setActionError(null);
    setPendingAction(null);
  };

  const handleConfirm = async (): Promise<void> => {
    if (!pendingAction || isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;

    try {
      await controlIngestion(pendingAction);
      setActionError(null);
      setPendingAction(null);
    } catch {
      setActionError(ACTION_ERROR_MESSAGES[pendingAction]);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <HHStack direction="row" spacing={2}>
      {canPause && (
        <HHButton
          hhVariant="variant-bypass"
          variant="outlined"
          data-testid={PAUSE_INGESTION_BUTTON_TEST_ID}
          onClick={(): void => selectAction(ControlAction.Pause)}
        >
          Pause
        </HHButton>
      )}
      {canResume && (
        <HHButton
          hhVariant="variant-bypass"
          variant="outlined"
          data-testid={RESUME_INGESTION_BUTTON_TEST_ID}
          onClick={(): void => selectAction(ControlAction.Resume)}
        >
          Resume
        </HHButton>
      )}
      {canCancel && (
        <HHButton
          hhVariant="variant-bypass"
          variant="outlined"
          color="error"
          data-testid={CANCEL_INGESTION_BUTTON_TEST_ID}
          onClick={(): void => selectAction(ControlAction.Cancel)}
        >
          Cancel
        </HHButton>
      )}
      {pendingAction && (
        <ControlActionConfirmationDialog
          open
          action={pendingAction}
          fileName={fileName}
          loading={isControlling}
          error={actionError}
          onConfirm={(): void => {
            handleConfirm();
          }}
          onCancel={handleCancelDialog}
        />
      )}
    </HHStack>
  );
};
